import React, { useState, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  Avatar,
  Snackbar,
  Alert,
  Link,
} from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth as firebaseAuth } from '../config/firebase';
import { AuthContext } from '../contexts/AuthContext';
import { updateMe } from '../services/authService';
import PageHeader from '../components/common/PageHeader';
import UserRoleBadge from '../components/users/UserRoleBadge';

const SettingsPage = () => {
  const { mongoUser, refreshUser } = useContext(AuthContext);
  const [name, setName] = useState(mongoUser?.name || '');
  const [phone, setPhone] = useState(mongoUser?.phone || '');
  const [saving, setSaving] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await updateMe({ name, phone });
      await refreshUser();
      setSnackbar({
        open: true,
        message: 'Profile updated successfully',
        severity: 'success',
      });
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to update profile';
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!mongoUser?.email) return;
    try {
      setResetLoading(true);
      await sendPasswordResetEmail(firebaseAuth, mongoUser.email);
      setSnackbar({
        open: true,
        message: `Password reset email sent to ${mongoUser.email}`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to send password reset email. Please try again.',
        severity: 'error',
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box>
      <PageHeader title="Settings" />

      {/* Profile Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              backgroundColor: 'primary.main',
              fontSize: '1.5rem',
            }}
          >
            {mongoUser?.name?.charAt(0)?.toUpperCase() || <PersonOutlinedIcon />}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {mongoUser?.name || 'User'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mongoUser?.email}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <UserRoleBadge role={mongoUser?.role} />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" gutterBottom>
          Edit Profile
        </Typography>
        <Box component="form" onSubmit={handleUpdateProfile} noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="small"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                size="small"
                placeholder="10-digit phone number"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                value={mongoUser?.email || ''}
                size="small"
                disabled
                helperText="Email cannot be changed"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Role"
                value={mongoUser?.role || ''}
                size="small"
                disabled
                helperText="Role is managed by administrators"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                sx={{ minWidth: 140 }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Change Password */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LockResetOutlinedIcon color="action" />
          <Typography variant="h6">Change Password</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          For security, password changes are handled through Firebase. Click the button below
          to receive a password reset email at <strong>{mongoUser?.email}</strong>.
        </Typography>
        <Button
          variant="outlined"
          onClick={handlePasswordReset}
          disabled={resetLoading}
          sx={{ mt: 2 }}
        >
          {resetLoading ? 'Sending...' : 'Send Password Reset Email'}
        </Button>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
